import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccessOptions } from '@shared/models/accessOptions.model';
import { Formation } from '@shared/models/formation.model';
import { FormationsGroups } from '@shared/models/formationsGroups.model';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { ANESTHETIST } from '../../const/globals.const';
import { Profile } from '../../models/profile.model';
import { FormatSeniorityPipe } from '../../pipes/format-seniority.pipe';
import { ErrorService } from '../../services/error.service';
import { ProfileService } from '../../services/profile.service';
import { UserService } from '../../services/user.service';
import { InstanceIdService } from '@shared/services/instance-id.service';

@Component({
  selector: 'app-profiles-picker-dialog',
  templateUrl: './profiles-picker-dialog.component.html',
  styleUrls: ['./profiles-picker-dialog.component.scss'],
  standalone: false,
})
export class ProfilesPickerDialogComponent implements OnInit {
  private instanceId: string;

  private profiles: any[] | undefined;

  public cycleName: string | undefined;

  public allProfiles: Profile[] | undefined;

  public isLoading: boolean = true;

  private page: number = 1;

  private pageSize: number = 7;

  public searchValue: string | undefined;

  private iadeProfiles: Profile[] | undefined;

  private anesthProfiles: Profile[] | undefined;

  private seniorProfiles: Profile[] | undefined;

  private juniorProfiles: Profile[] | undefined;

  private internProfiles: Profile[] | undefined;

  private nurseprofiles: Profile[] | undefined;

  private isAllSelectedBySeniotity: { [key: string]: boolean } = {};

  public profileListName = '';

  public profileListNames: string[] = [];

  public profileListOptions = [''];

  public profilesSeniorities = [''];

  public profilesSeniority = '';

  public selectedProfilesSeniorities: string[] = [];

  public profilesStatus: string[] = ['Titulaire', 'Vacataire'];

  public selectedProfilesStatus: string[] = ['Titulaire', 'Vacataire'];

  public position: string | undefined;

  public formGroup: FormGroup | undefined;

  public profilesFormElements: any;

  public accessOptions: AccessOptions | undefined;

  public gap: string = '10px';

  public nursesFormations: string[] = [];

  public allFormationsGroups: FormationsGroups[] = [];

  public isUsingSeniorities: boolean = false;

  private profilesSenioritiesObj: { [key: string]: string } = {
    Senior: 'Séniors',
    Junior: 'Juniors',
    Interne: 'Internes',
    Séniors: 'Senior',
    Juniors: 'Junior',
    Internes: 'Interne',
  };

  private isNight: boolean = false;

  private selectedAllFormation: FormationsGroups = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly profileService: ProfileService,
    private readonly dialogRef: MatDialogRef<ProfilesPickerDialogComponent>,
    private readonly errorService: ErrorService,
    private readonly userService: UserService,
    private readonly formBuilder: FormBuilder,
    private readonly formatSeniority: FormatSeniorityPipe,
    private readonly router: Router,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.accessOptions = this.userService.getAccessOptionsOfPage(this.router.url);
    if (Array.isArray(this.accessOptions?.seniority) && this.accessOptions.seniority.length) {
      this.profilesSeniorities = this.accessOptions?.seniority.map((s: string) => this.profilesSenioritiesObj[s]);
      if (this.profilesSeniorities.length > 0) {
        this.profilesSeniority = this.profilesSeniorities[0];
      }
    }
  }

  async ngOnInit() {
    this.isNight = this.data?.isNight;
    this.allFormationsGroups = (await this.userService.getAllFormationsGroups()).filter(
      (formationGroups: FormationsGroups) => {
        return formationGroups.typeMode && ['nurse', 'iade', 'anesth'].includes(formationGroups.typeMode);
      }
    );
    this.allFormationsGroups = this.userService.removeOverlappingFormationsFromDefaults(this.allFormationsGroups);

    if (
      Array.isArray(this.accessOptions?.typeOfCycles) &&
      this.accessOptions.typeOfCycles.length > 0 &&
      this.allFormationsGroups.length > 0
    ) {
      this.profileListOptions = this.accessOptions.typeOfCycles.map((typeOfCycle: string) => {
        return (
          this.allFormationsGroups.find(
            (formationGroup: FormationsGroups) => String(formationGroup._id) === String(typeOfCycle)
          )?.displayName ?? ''
        );
      });
    }

    const cycleFormationsGroup = this.allFormationsGroups.find((formationsGroup: FormationsGroups) => {
      return String(this.data?.cycle?.position) === String(formationsGroup._id);
    });

    this.profileListName = cycleFormationsGroup?.displayName ?? '';
    this.position = this.profileListName;
    this.cycleName = this.data.cycle ? this.data.cycle.name : '';
    this.profilesSeniorities.forEach((seniority: string) => {
      this.isAllSelectedBySeniotity[seniority] = false;
    });
    if (Array.isArray(this.data.cycle.seniorities) && this.data.cycle.seniorities.length > 0) {
      this.selectedProfilesSeniorities = this.data.cycle.seniorities.map((s: string) => this.profilesSenioritiesObj[s]);
    }
    await this.getProfiles();
    this.createForm();
    this.initForm();
    this.onProfilesSelectionChange();
    await this.initFormationGroups();
  }

  async getProfiles() {
    const formations = JSON.stringify(
      (await this.userService.getAccessOptionsPositionsByPositions(this.accessOptions?.typeOfCycles, true)).map(
        (formation: Formation) => formation._id
      )
    );

    this.subscriptionManager
      .createRequest(
        this.profileService.getProfilesByPosition(
          'All',
          this.page,
          this.pageSize,
          '',
          false,
          true,
          true,
          undefined,
          this.isNight,
          false,
          false,
          undefined,
          formations
        ),
        'getProfilesByPosition',
        this.instanceId
      )
      .subscribe({
        next: (res: any) => {
          this.allProfiles = res.docs;
          this.formatProfiles();
          this.anesthProfiles = [...(this.allProfiles?.filter((p: Profile) => p.position === ANESTHETIST) ?? [])];
          this.seniorProfiles = [...this.anesthProfiles.filter((p: Profile) => p.seniority === 'Senior')];
          this.juniorProfiles = [...this.anesthProfiles.filter((p: Profile) => p.seniority === 'Junior')];
          this.internProfiles = [...this.anesthProfiles.filter((p: Profile) => p.seniority === 'Interne')];
          this.iadeProfiles = [
            ...(this.allProfiles?.filter((p: Profile) => p.position?.toLowerCase() === 'iade') ?? []),
          ];
          this.nurseprofiles = [
            ...(this.allProfiles?.filter((p: Profile) => this.nursesFormations.includes(p.position ?? '')) ?? []),
          ];

          this.getProfileListType(this.position ?? '');
          this.isLoading = false;
          if (this.profiles?.length === this.profiles?.filter((x: Profile) => x.checked).length) {
            this.isAllSelectedBySeniotity[this.profilesSeniority] = true;
          }
          this.initForm();
        },
        error: (error: any) => {
          this.isLoading = false;
          this.errorService.handleError(error);
        },
      });
  }

  async initFormationGroups(): Promise<void> {
    const nursesFormationsTmp = await this.userService.getFormationsByGroupName(undefined, 'NURSES');
    this.nursesFormations = nursesFormationsTmp.map((formation: any) => formation.name);
  }

  formatProfiles() {
    const selProfiles = this.data.selectedProfiles;
    if (!selProfiles) {
      return;
    }
    this.allProfiles?.forEach((el: Profile) => {
      const index = selProfiles.findIndex((selProf: Profile) => selProf._id == el._id);
      if (index != -1) {
        el.checked = true;
      }
    });
  }

  save() {
    const body = {
      profiles: this.allProfiles?.filter((el: Profile) => el.checked),
      name: this.cycleName,
      seniorities:
        this.selectedProfilesSeniorities.length > 0
          ? this.selectedProfilesSeniorities.map((s: string) => this.profilesSenioritiesObj[s])
          : null,
    };

    this.dialogRef.close(body);
  }

  createForm() {
    this.formGroup = this.formBuilder.group({
      profiles: [''],
    });
  }

  initForm() {
    if (Array.isArray(this.profiles)) {
      this.profilesFormElements = [
        { label: 'Tout sélectionner', value: 'all', isChecked: this.profiles.every((el: Profile) => el.checked) },
      ];
      for (const profile of this.profiles) {
        this.profilesFormElements.push({
          label:
            `${this.capitalize(profile.firstName)} ${this.capitalize(profile.lastName)} - ` +
            (this.position === 'Anesthésiste' && this.profileListName === 'Anesthésiste'
              ? this.formatSeniority.transform(profile.seniority)
              : profile.position) +
            `  - ${this.capitalize(profile?.residency ?? '')}`,
          isChecked: profile.checked,
          value: profile._id,
        });
      }
      this.formGroup
        ?.get('profiles')
        ?.setValue(this.profilesFormElements.filter((el: any) => el.isChecked).map((el: any) => el.value));
    }
  }

  onProfilesSelectionChange() {
    if (this.formGroup?.get('profiles')) {
      this.subscriptionManager
        .createRequest(this.formGroup.get('profiles')!.valueChanges, 'profilesForm', this.instanceId)
        .subscribe({
          next: (val: any) => {
            if (!this.isAllSelectedBySeniotity[this.profilesSeniority] && val.includes('all')) {
              this.isAllSelectedBySeniotity[this.profilesSeniority] = true;
              this.profiles?.forEach((profile: Profile) => {
                profile.checked = true;
              });
              this.profilesFormElements?.forEach((profile: any) => {
                profile.isChecked = true;
              });
              this.selectAllChange(true, this.profilesSeniority);
              return;
            }

            if (this.isAllSelectedBySeniotity[this.profilesSeniority] && !val.includes('all')) {
              this.isAllSelectedBySeniotity[this.profilesSeniority] = false;
              this.profiles?.forEach((profile: Profile) => {
                profile.checked = false;
              });
              this.profilesFormElements?.forEach((profile: any) => {
                profile.isChecked = false;
              });
              this.selectAllChange(false, this.profilesSeniority);
              return;
            }

            if (
              !this.isAllSelectedBySeniotity[this.profilesSeniority] &&
              this.profilesFormElements
                ?.filter((el: any) => el.value !== 'all')
                .every((profileElement: any) => profileElement.isChecked)
            ) {
              this.isAllSelectedBySeniotity[this.profilesSeniority] = true;
              this.profilesFormElements.find((profileElement: any) => profileElement.value === 'all').isChecked = true;
            } else {
              if (this.profilesFormElements) {
                this.isAllSelectedBySeniotity[this.profilesSeniority] = false;
                this.profilesFormElements.find((profileElement: any) => profileElement.value === 'all').isChecked =
                  false;
              }
            }

            this.profiles?.forEach((profile: Profile) => {
              profile.checked = val.includes(profile._id);
            });

            this.onProfileSelectionChange(this.profilesSeniority);
          },

          error: (error: any) => {
            this.errorService.handleError(error);
          },
        });
    }
  }

  onProfileSelectionChange(seniority: string): void {
    this.isAllSelectedBySeniotity[seniority] =
      this.profiles?.length === this.profiles?.filter((x: Profile) => x.checked).length;
  }

  getSelectedIDS(profiles: any[]): string[] {
    const selectProfiles = profiles.filter((el: any) => el.checked);
    return selectProfiles.map((el: any) => el._id);
  }

  doFilter(value: string) {
    this.getProfileListType(null);

    this.profiles = this.profiles?.filter((profile: Profile) => {
      const re = new RegExp(value, 'gi');
      return profile.firstName?.match(re) != null || profile.lastName?.match(re) != null;
    });
    if (this.profilesSeniority) {
      this.isAllSelectedBySeniotity[this.profilesSeniority] =
        this.profiles?.every((profile: Profile) => profile.checked) ?? false;
    }
    this.initForm();
  }

  selectAllChange(checked: boolean, seniority: string) {
    const isChecked = checked;
    this.isAllSelectedBySeniotity[seniority] = checked;
    this.profiles?.forEach((profile: Profile) => {
      profile.checked = isChecked;
    });
  }

  capitalize(s: string): string {
    const text = s.toLowerCase();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  async getProfileListType(event: string | null) {
    // event : 'Anesthésiste' | 'Séniors' | 'Juniors' | 'Internes' | 'IADE' | null
    let profileListNamesTmp: string[] = [];

    if (event) {
      if (this.profileListOptions.includes(event)) {
        //Formation

        this.profileListName = event;
        const formationGroups = this.allFormationsGroups.find(
          (formationGroup: FormationsGroups) => formationGroup.displayName === event
        );
        this.isUsingSeniorities = formationGroups?.isUsingSeniorities ?? false;
        if (this.isUsingSeniorities) {
          if (!this.selectedProfilesSeniorities.includes('Séniors')) {
            this.selectedProfilesSeniorities.push('Séniors');
          }
          if (!this.profileListNames.includes('Séniors')) {
            this.profileListNames.push('Séniors');
          }
        }

        if (!this.profileListNames) {
          this.profileListNames = [];
        }

        //Does formation already selected?
        const indexTmp = this.profileListNames.indexOf(event);

        if (indexTmp > -1) {
          this.profileListNames.splice(indexTmp, 1);
          if (this.isUsingSeniorities) {
            this.profileListNames = this.profileListNames.filter((item: string) => item !== 'Séniors');
            this.selectedProfilesSeniorities.length = 0;
          }
        } else {
          this.profileListNames.push(event);
        }
        this.selectedAllFormation =
          this.allFormationsGroups.find((formationGroup: FormationsGroups) =>
            event ? formationGroup.displayName === event : formationGroup.displayName === profileListNamesTmp?.[0]
          ) || {};
      } else if (['Séniors', 'Juniors', 'Internes'].includes(event)) {
        //Seniority
        //Does formation already selected?
        const indexSeniorityTmp = this.selectedProfilesSeniorities.indexOf(event);

        if (indexSeniorityTmp > -1) {
          this.selectedProfilesSeniorities?.splice(indexSeniorityTmp, 1);
        } else {
          this.selectedProfilesSeniorities?.push(event);
        }

        this.profilesSeniority = event;
      } else {
        //Status
        const index = this.selectedProfilesStatus?.indexOf(event);

        if (index > -1) {
          this.selectedProfilesStatus?.splice(index, 1);
        } else {
          this.selectedProfilesStatus?.push(event);
        }
      }
    }

    // deselect Anesth if no seniority selected
    if (this.selectedProfilesSeniorities?.length === 0) {
      this.isUsingSeniorities = false;
      this.profileListNames = this.profileListNames.filter((item: string) => {
        const formationGroups = this.allFormationsGroups.find(
          (formationGroup: FormationsGroups) => formationGroup.displayName === item
        );
        return !formationGroups?.isUsingSeniorities;
      });
    }

    // create list of types of profiles to display
    profileListNamesTmp = [...(this.profileListNames || [])];
    const anesthSelected = this.profileListNames.some((profileName: string) => {
      const formationGroups = this.allFormationsGroups.find(
        (formationGroup: FormationsGroups) => formationGroup.displayName === profileName
      );
      return formationGroups?.isUsingSeniorities;
    });
    if (anesthSelected) {
      profileListNamesTmp.push(...this.selectedProfilesSeniorities);
    }

    // delete seniorities from formations names array
    this.profileListNames = this.profileListNames.filter((item: string) => this.profileListOptions.includes(item));

    // delete 'Anesthésiste' from array
    profileListNamesTmp = profileListNamesTmp.filter((profileName: string) => {
      const formationGroups = this.allFormationsGroups.find(
        (formationGroup: FormationsGroups) => formationGroup.displayName === profileName
      );
      return !formationGroups?.isUsingSeniorities;
    });

    this.profiles = [];

    profileListNamesTmp = [...new Set(profileListNamesTmp)];

    profileListNamesTmp?.forEach((name: string) => {
      const typeMode =
        this.allFormationsGroups.find((formationGroup: FormationsGroups) => formationGroup.displayName === name)
          ?.typeMode ?? name;
      switch (typeMode) {
        case 'nurse':
          this.profiles = [...(this.profiles || []), ...(this.nurseprofiles ?? [])];
          break;
        case 'anesth':
          this.profiles = [...(this.profiles || []), ...(this.anesthProfiles ?? [])];
          break;
        case 'Séniors':
          this.profiles = [...(this.profiles || []), ...(this.seniorProfiles ?? [])];
          break;
        case 'Juniors':
          this.profiles = [...(this.profiles || []), ...(this.juniorProfiles ?? [])];
          break;
        case 'Internes':
          this.profiles = [...(this.profiles || []), ...(this.internProfiles ?? [])];
          break;
        case 'iade':
          this.profiles = [...(this.profiles || []), ...(this.iadeProfiles ?? [])];
          break;
        default:
      }
    });

    //filter by status
    if (this.selectedProfilesStatus.length < 2) {
      this.profiles = this.profiles.filter((item: any) => this.selectedProfilesStatus.includes(item.residency));
    }

    if (this.selectedAllFormation) {
      const formations = (await this.userService.getFormationsOfFormationsGroup(this.selectedAllFormation, true)).map(
        (formation: any) => formation.name
      );
      this.profiles = this.profiles?.filter((profile: Profile) => formations.includes(profile.position));
    }

    //uncheck all profiles
    if (['Séniors', 'Juniors', 'Internes', 'Titulaire', 'Vacataire'].includes(event ?? '')) {
      this.allProfiles?.forEach((profile: Profile) => {
        profile.checked = false;
      });
    }

    this.initForm();
  }

  isOptionDisabled(option: string): boolean {
    if (!this.profileListNames.length) {
      return false;
    }

    const isSelected = this.profileListNames.includes(option);

    return !isSelected;
  }

  isProfileSelected(profileValue: string): boolean {
    const selectedProfiles = this.formGroup?.get('profiles')?.value || [];
    if (profileValue === 'all') {
      const allProfileValues = this.profilesFormElements
        .filter((profile: any) => profile.value !== 'all')
        .map((profile: any) => profile.value);

      return allProfileValues.every((value: any) => selectedProfiles.includes(value));
    }
    return selectedProfiles.includes(profileValue);
  }

  onProfileChange(event: any, profileValue: string) {
    const isChecked = event.checked;
    const currentValues = this.formGroup?.get('profiles')?.value || [];

    let newValues;
    if (profileValue === 'all') {
      if (isChecked) {
        newValues = this.profilesFormElements
          .filter((profile: any) => profile.value !== 'all')
          .map((profile: any) => profile.value);
      } else {
        newValues = [];
      }
    } else {
      if (isChecked) {
        newValues = [...currentValues, profileValue];
      } else {
        newValues = currentValues.filter((value: string) => value !== profileValue);
      }
    }

    this.formGroup?.get('profiles')?.setValue(newValues);
  }

  close(): void {
    this.dialogRef.close();
  }
}
